import { type ReactNode, useMemo } from "react";

import { Header } from "../components";
import {
  LogoStandaloneIcon,
  LogoWithText,
  CollaborationIcon,
  ConflictIcon,
  CustomerIcon,
  EdixIcon,
  ENpsIcon,
  PsychSafetyIcon,
  StagilityIcon,
  StrategyIcon,
} from "../icons";

const LogoCard = ({
  label,
  bgColor,
  children,
}: {
  label: string;
  bgColor: string;
  children: ReactNode;
}) => (
  <div className="logo-card">
    <div className="logo-card-container" style={{ background: bgColor }}>
      <div className="logo-card-content">{children}</div>
    </div>
    <p className="logo-card-label">{label}</p>
  </div>
);

const LogoSymbolCard = ({
  label,
  children,
  icon,
  isWhite,
}: {
  label: string;
  children?: ReactNode;
  icon?: (() => ReactNode) | ReactNode;
  isWhite?: boolean;
}) => {
  const iconContent = useMemo(() => {
    try {
      return typeof icon === "function" ? icon() : icon || children;
    } catch (error) {
      console.error("Error rendering icon:", error);
      return children || null;
    }
  }, [icon, children]);

  return (
    <div className="logo-symbol-card">
      <div
        className={`logo-symbol-card-container${
          isWhite ? " logo-symbol-card-container-white" : ""
        }`}
      >
        <div className="logo-symbol-card-content">{iconContent}</div>
      </div>
      <p className="logo-symbol-card-label">{label}</p>
    </div>
  );
};

const LogoButton = ({
  label,
  variant,
}: {
  label: string;
  variant: "default" | "hover" | "pressed";
}) => {
  const getButtonClassName = () => {
    const baseClass = "logo-button";
    const variantClass = `logo-button--${variant}`;
    return `${baseClass} ${variantClass}`;
  };

  return (
    <div className="logo-button-container">
      <button className={getButtonClassName()} type="button">
        Start
      </button>
      <p className="logo-button-label">{label}</p>
    </div>
  );
};

const LogoIconCard = ({
  label,
  icon,
}: {
  label: string;
  icon: (() => ReactNode) | ReactNode;
}) => {
  const iconContent = useMemo(() => {
    try {
      return typeof icon === "function" ? icon() : icon;
    } catch (error) {
      console.error("Error rendering icon:", error);
      return null;
    }
  }, [icon]);

  return (
    <div className="logo-icon-card">
      <div className="logo-icon-card-icon">{iconContent}</div>
      <p className="logo-icon-card-label">{label}</p>
    </div>
  );
};

export const LogoStyleGuide = () => (
  <>
    <Header title="CLX Logos" />

    <div className="logo-guide-page">
      <section>
        <h2 className="logo-guide-section-title">Primary Logos</h2>
        <div className="logo-guide-cards-row">
          <LogoCard label="CLX Logo Savvy 900" bgColor="var(--clx-savvy-white)">
            <LogoWithText variant="savvy-900" />
          </LogoCard>

          <LogoCard
            label="CLX Logo Savvy White"
            bgColor="var(--clx-savvy-black)"
          >
            <LogoWithText variant="savvy-white" />
          </LogoCard>

          <LogoCard
            label="CLX Logo Savvy Dark Gradient"
            bgColor="var(--clx-savvy-light-gradient)"
          >
            <LogoWithText variant="savvy-dark-gradient" />
          </LogoCard>
        </div>
      </section>

      <section>
        <h2 className="logo-guide-section-title">Primary Logo symbols</h2>
        <div className="logo-guide-cards-row">
          <LogoSymbolCard
            label="CLX Savvy White"
            isWhite
            icon={() => <LogoStandaloneIcon variant="savvy-white" size={150} />}
          />

          <LogoSymbolCard
            label="CLX Savvy Accent"
            icon={() => (
              <LogoStandaloneIcon variant="savvy-accent" size={150} />
            )}
          />

          <LogoSymbolCard
            label="CLX Savvy 300"
            icon={() => <LogoStandaloneIcon variant="savvy-300" size={150} />}
          />

          <LogoSymbolCard
            label="CLX Savvy 500"
            icon={() => <LogoStandaloneIcon variant="savvy-500" size={150} />}
          />

          <LogoSymbolCard
            label="CLX Savvy 700"
            icon={() => <LogoStandaloneIcon variant="savvy-700" size={150} />}
          />

          <LogoSymbolCard
            label="CLX Savvy 900"
            icon={() => <LogoStandaloneIcon variant="savvy-900" size={150} />}
          />

          <LogoSymbolCard
            label="CLX Savvy Black"
            icon={() => <LogoStandaloneIcon variant="savvy-black" size={150} />}
          />
        </div>
      </section>

      <div className="favicon-buttons-row">
        <section className="logo-guide-favicon-section">
          <h2 className="logo-guide-section-title">Favicon</h2>
          <div className="favicon-box">
            <LogoStandaloneIcon variant="savvy-white" size={120} />
          </div>
        </section>

        <section className="logo-guide-buttons-section">
          <h2 className="logo-guide-section-title">Buttons</h2>
          <div className="logo-guide-buttons-row">
            <LogoButton label="Default" variant="default" />
            <LogoButton label="Hover" variant="hover" />
            <LogoButton label="Pressed" variant="pressed" />
          </div>
        </section>
      </div>

      <section>
        <h2 className="logo-guide-section-title">Cube Icons</h2>
        <div className="cube-icons-grid">
          <LogoIconCard label="Strategy & Vision" icon={<StrategyIcon />} />

          <LogoIconCard
            label="Stagility (Stability & Agility)"
            icon={<StagilityIcon />}
          />

          <LogoIconCard label="Customer Centricity" icon={<CustomerIcon />} />

          <LogoIconCard
            label="Psychological Safety"
            icon={<PsychSafetyIcon />}
          />

          <LogoIconCard label="Conflict" icon={<ConflictIcon />} />

          <LogoIconCard label="Collaboration" icon={<CollaborationIcon />} />
        </div>
        <div className="cube-icons-grid cube-icons-second-row">
          <LogoIconCard label="EDIX" icon={<EdixIcon />} />
          <LogoIconCard label="eNPS" icon={<ENpsIcon />} />
        </div>
      </section>
    </div>
  </>
);
